import { createDateFormatter } from "@nexus/ui";
import type { CustomSeriesOption } from "echarts/charts";
import {
  categoryNoDataAreas,
  escapeHtml,
  NO_DATA_LABEL,
  periodBounds,
  periodDays,
  positionChartTooltip,
  trimMissing,
  type MissingDataMode,
  type SeriesPeriod,
} from "@nexus/widget-kit";
import { withAlpha } from "@nexus/widget-sdk";
import type { ChartColors } from "@nexus/widget-sdk";
import type { AccountWaterfallPoint } from "./types";

type StepKind = "observed" | "lost" | "newAccounts";
export interface WaterfallStep {
  day: string;
  kind: StepKind;
  start: number | null;
  end: number | null;
  value: number | null;
  showDate: boolean;
  point: AccountWaterfallPoint | null;
}
export interface WaterfallLabels {
  observed: string;
  lost: string;
  newAccounts: string;
}
export interface WaterfallDayBand {
  startIndex: number;
  endIndex: number;
  dayIndex: number;
}
export type WaterfallArrowStyle = "off" | "inside" | "shape";

const number = new Intl.NumberFormat("en");
const dateLabel = createDateFormatter(
  "utc",
  {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  },
  "en",
);

/** A day the source could not count completely: `null` accounts, never zero. */
export const isMissingDay = (point: AccountWaterfallPoint) => point.accounts === null;

/**
 * The days the chart shows: every day of the requested period (Show gap), or
 * only those between the first and last measured day (Fit to data).
 */
export function accountDays(
  points: readonly AccountWaterfallPoint[],
  period: SeriesPeriod | null | undefined,
  mode: MissingDataMode,
): AccountWaterfallPoint[] {
  const byDay = new Map(points.map((point) => [point.day, point]));
  const bounds = periodBounds(period);
  const days = [...new Set([...(bounds ? periodDays(bounds) : []), ...byDay.keys()])].sort();
  const all = days.map(
    (day) =>
      byDay.get(day) ?? {
        day,
        accounts: null,
        previous_accounts: null,
        new_accounts: null,
        lost_accounts: null,
      },
  );
  return mode === "fit" ? trimMissing(all, isMissingDay) : all;
}

export function summarizeAccounts(points: readonly AccountWaterfallPoint[]) {
  const sorted = [...points].sort((left, right) => left.day.localeCompare(right.day));
  const available = sorted.filter(
    (point): point is AccountWaterfallPoint & { accounts: number } => point.accounts !== null,
  );
  return {
    // The latest measured day; a trailing day without data says nothing.
    latest: available[available.length - 1] ?? null,
    peak: available.reduce<(typeof available)[number] | null>(
      (best, point) => (!best || point.accounts > best.accounts ? point : best),
      null,
    ),
    dailyAverage: available.length
      ? available.reduce((total, point) => total + point.accounts, 0) / available.length
      : null,
  };
}

export function waterfallSteps(
  points: readonly AccountWaterfallPoint[],
  showStartingBalance = true,
): WaterfallStep[] {
  const sorted = [...points].sort((left, right) => left.day.localeCompare(right.day));
  if (!sorted.length) return [];
  const steps: WaterfallStep[] = [];
  if (showStartingBalance) {
    const previous = new Date(`${sorted[0].day}T00:00:00Z`);
    previous.setUTCDate(previous.getUTCDate() - 1);
    steps.push({
      day: previous.toISOString().slice(0, 10),
      kind: "observed",
      start: 0,
      end: sorted[0].previous_accounts,
      value: sorted[0].previous_accounts,
      showDate: true,
      point: null,
    });
  }
  for (const point of sorted) {
    steps.push(
      {
        day: point.day,
        kind: "lost",
        start: point.previous_accounts,
        end:
          point.previous_accounts !== null && point.lost_accounts !== null
            ? point.previous_accounts - point.lost_accounts
            : null,
        value: point.lost_accounts,
        showDate: false,
        point,
      },
      {
        day: point.day,
        kind: "newAccounts",
        start:
          point.accounts !== null && point.new_accounts !== null
            ? point.accounts - point.new_accounts
            : null,
        end: point.accounts,
        value: point.new_accounts,
        showDate: true,
        point,
      },
      {
        day: point.day,
        kind: "observed",
        start: 0,
        end: point.accounts,
        value: point.accounts,
        showDate: false,
        point,
      },
    );
  }
  return steps;
}

export function waterfallDayBands(steps: readonly WaterfallStep[]): WaterfallDayBand[] {
  const bands: WaterfallDayBand[] = [];
  steps.forEach((step, index) => {
    if (step.kind === "lost") {
      bands.push({ startIndex: index, endIndex: index + 2, dayIndex: bands.length });
    }
  });
  return bands;
}

export function waterfallArrowPoints(
  start: readonly [number, number],
  end: readonly [number, number],
  categoryWidth: number,
): [number, number][] | null {
  const segmentHeight = Math.abs(end[1] - start[1]);
  if (segmentHeight === 0) return null;

  const centerX = start[0];
  const top = Math.min(start[1], end[1]);
  const bottom = Math.max(start[1], end[1]);
  const headHalfWidth = categoryWidth * 0.36;
  const shaftHalfWidth = categoryWidth * 0.18;
  const headHeight = Math.min(12, segmentHeight * 0.38);

  return end[1] < start[1]
    ? [
        [centerX, top],
        [centerX + headHalfWidth, top + headHeight],
        [centerX + shaftHalfWidth, top + headHeight],
        [centerX + shaftHalfWidth, bottom],
        [centerX - shaftHalfWidth, bottom],
        [centerX - shaftHalfWidth, top + headHeight],
        [centerX - headHalfWidth, top + headHeight],
      ]
    : [
        [centerX, bottom],
        [centerX + headHalfWidth, bottom - headHeight],
        [centerX + shaftHalfWidth, bottom - headHeight],
        [centerX + shaftHalfWidth, top],
        [centerX - shaftHalfWidth, top],
        [centerX - shaftHalfWidth, bottom - headHeight],
        [centerX - headHalfWidth, bottom - headHeight],
      ];
}

export function buildWaterfallOption(
  points: readonly AccountWaterfallPoint[],
  colors: ChartColors,
  options: {
    labels: WaterfallLabels;
    showLegend: boolean;
    showStartingBalance: boolean;
    arrowStyle: WaterfallArrowStyle;
    motion: boolean;
  },
) {
  const steps = waterfallSteps(points, options.showStartingBalance);
  const dayBands = waterfallDayBands(steps);
  const categories = steps.map((_step, index) => String(index));
  const missing = steps.map((step) => step.point !== null && isMissingDay(step.point));
  const kinds = ["observed", "lost", "newAccounts"] as const;
  const palette = {
    observed: withAlpha(colors.label, 0.3),
    lost: colors.accent,
    newAccounts: colors.series[3],
  };
  const labelSize = colors.typography?.labelSize ?? 12;
  const date = (day: string) => dateLabel.format(new Date(`${day}T00:00:00Z`));
  const measured = (step: WaterfallStep) =>
    step.start !== null && step.end !== null && step.value !== null;
  const signed = (step: WaterfallStep) =>
    step.value === null
      ? ""
      : `${step.value === 0 || step.kind === "observed" ? "" : step.kind === "lost" ? "-" : "+"}${number.format(step.value)}`;
  const renderDayBand: NonNullable<CustomSeriesOption["renderItem"]> = (params, api) => {
    const dayIndex = Number(api.value(3));
    const startIndex = Number(api.value(0));
    const first = api.coord([startIndex, 0])[0];
    const next = api.coord([startIndex + 1, 0])[0];
    const last = api.coord([Number(api.value(1)), 0])[0];
    const categoryWidth = Math.abs(next - first);
    const left = Math.min(first, last) - categoryWidth / 2;
    const { y, height } = params.coordSys as typeof params.coordSys & {
      y: number;
      height: number;
    };
    return {
      type: "group",
      children: [
        {
          type: "rect",
          shape: { x: left, y, width: Math.abs(last - first) + categoryWidth, height },
          style: { fill: withAlpha(colors.label, dayIndex % 2 === 0 ? 0.045 : 0.018) },
        },
        {
          type: "line",
          shape: { x1: left, y1: y, x2: left, y2: y + height },
          style: { stroke: withAlpha(colors.label, 0.18), lineWidth: 1 },
        },
      ],
    };
  };
  const renderAnnotations: NonNullable<CustomSeriesOption["renderItem"]> = (_params, api) => {
    const index = Number(api.value(0));
    const step = steps[index];
    if (!step || !measured(step)) return;
    const start = api.coord([index, step.start!]);
    const end = api.coord([index, step.end!]);
    const band = Math.abs(api.coord([index + 1, 0])[0] - api.coord([index, 0])[0]);
    const next = steps[index + 1];
    const nextValue = next?.kind === "observed" ? next.end : next?.start;
    const connectors =
      next && measured(next) && nextValue === step.end
        ? [
            {
              type: "line" as const,
              shape: { x1: end[0] + band * 0.35, y1: end[1], x2: end[0] + band * 0.65, y2: end[1] },
              style: { stroke: withAlpha(colors.label, 0.45), lineWidth: 1, lineDash: [3, 3] },
            },
          ]
        : [];
    if (options.arrowStyle === "off" || step.kind === "observed") {
      return { type: "group", children: connectors };
    }
    if (options.arrowStyle === "shape") {
      const arrowPoints = waterfallArrowPoints([start[0], start[1]], [end[0], end[1]], band);
      if (!arrowPoints) return { type: "group", children: connectors };
      return {
        type: "group",
        children: [
          ...connectors,
          {
            type: "polygon",
            shape: { points: arrowPoints },
            style: { fill: palette[step.kind] },
          },
        ],
      };
    }
    if (Math.abs(end[1] - start[1]) < 22) {
      return { type: "group", children: connectors };
    }
    const direction = Math.sign(end[1] - start[1]);
    const tip = end[1] - direction * 5;
    const head = Math.min(5, band * 0.15);
    const style = { stroke: colors.tooltipBg, lineWidth: 1.6, opacity: 0.85, fill: "none" };
    return {
      type: "group",
      children: [
        ...connectors,
        {
          type: "line",
          shape: { x1: start[0], y1: start[1] + direction * 5, x2: end[0], y2: tip },
          style,
        },
        {
          type: "polyline",
          shape: {
            points: [
              [end[0] - head, tip - direction * head],
              [end[0], tip],
              [end[0] + head, tip - direction * head],
            ],
          },
          style,
        },
      ],
    };
  };
  return {
    animation: options.motion,
    animationDuration: 350,
    textStyle: { fontFamily: colors.typography?.fontFamily, fontSize: labelSize },
    grid: {
      top: options.showLegend ? 42 : 24,
      right: 12,
      bottom: steps.length > 31 ? 44 : 8,
      left: 8,
      containLabel: true,
    },
    legend: {
      show: options.showLegend,
      selectedMode: false,
      data: kinds.map((kind) => ({
        name: kind,
        icon: "rect",
        itemStyle: { color: palette[kind] },
      })),
      top: 0,
      type: "scroll",
      icon: "rect",
      itemWidth: 12,
      itemHeight: 8,
      formatter: (name: string) => options.labels[name as StepKind] ?? name,
      textStyle: { color: colors.label, fontSize: labelSize },
    },
    xAxis: {
      type: "category",
      data: categories,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: colors.axisLine } },
      axisLabel: {
        color: colors.label,
        fontSize: labelSize,
        lineHeight: labelSize + 2,
        interval: 0,
        hideOverlap: true,
        formatter: (value: string) => {
          const step = steps[Number(value)];
          if (!step?.showDate) return "";
          return date(step.day);
        },
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      minInterval: 1,
      splitNumber: 4,
      axisLabel: { color: colors.label, fontSize: labelSize },
      splitLine: { lineStyle: { color: colors.grid } },
    },
    dataZoom:
      steps.length > 31
        ? [
            {
              type: "inside",
              filterMode: "none",
              startValue: steps.length - 31,
              endValue: steps.length - 1,
            },
            {
              type: "slider",
              filterMode: "none",
              height: 12,
              bottom: 0,
              showDetail: false,
              brushSelect: false,
            },
          ]
        : [],
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      textStyle: { color: colors.tooltipText },
      position: (
        point: number[],
        _params: unknown,
        _dom: unknown,
        _rect: unknown,
        size: { contentSize: number[]; viewSize: number[] },
      ) => positionChartTooltip(point, size),
      formatter: (params: unknown) => {
        const entry = (Array.isArray(params) ? params[0] : params) as
          | { dataIndex?: number }
          | undefined;
        const step = steps[entry?.dataIndex ?? -1];
        if (!step) return "";
        const heading = `<div style="font-weight:600;margin-bottom:6px">${escapeHtml(step.day)} · UTC</div>`;
        if (step.point && isMissingDay(step.point)) return `${heading}<div>${NO_DATA_LABEL}</div>`;
        const row = (label: string, value: number | null) =>
          `<div style="display:flex;justify-content:space-between;gap:20px"><span>${escapeHtml(label)}</span><b>${value === null ? "Unavailable" : number.format(value)}</b></div>`;
        return (
          heading +
          (step.point
            ? row("Previous day", step.point.previous_accounts) +
              row(options.labels.lost, step.point.lost_accounts) +
              row(options.labels.newAccounts, step.point.new_accounts) +
              row(options.labels.observed, step.point.accounts)
            : row(options.labels.observed, step.value))
        );
      },
    },
    series: [
      {
        type: "custom",
        silent: true,
        clip: true,
        z: -10,
        tooltip: { show: false },
        encode: { x: [0, 1], y: 2 },
        renderItem: renderDayBand,
        data: dayBands.map(({ startIndex, endIndex, dayIndex }) => [
          startIndex,
          endIndex,
          0,
          dayIndex,
        ]),
      },
      {
        type: "bar",
        stack: "accounts",
        silent: true,
        itemStyle: { color: "transparent" },
        emphasis: { disabled: true },
        tooltip: { show: false },
        markArea: { silent: true, data: categoryNoDataAreas(categories, missing, colors) },
        data: steps.map((step) => (measured(step) ? Math.min(step.start!, step.end!) : null)),
      },
      ...kinds.map((kind) => ({
        name: kind,
        type: "bar",
        stack: "accounts",
        barWidth: "70%",
        itemStyle: {
          color:
            options.arrowStyle === "shape" && kind !== "observed" ? "transparent" : palette[kind],
          ...(kind === "observed" ? { borderColor: colors.label, borderWidth: 1 } : {}),
        },
        label: {
          show: true,
          position: kind === "lost" ? "bottom" : "top",
          color: colors.tooltipText,
          fontSize: labelSize,
          formatter: (params: { dataIndex: number }) => signed(steps[params.dataIndex]),
        },
        labelLayout: { hideOverlap: true },
        data: steps.map((step) =>
          step.kind === kind && measured(step) ? Math.abs(step.end! - step.start!) : null,
        ),
      })),
      {
        type: "custom",
        silent: true,
        clip: true,
        z: 5,
        tooltip: { show: false },
        encode: { x: 0, y: [1, 2] },
        renderItem: renderAnnotations,
        data: steps.map((step, index) => [
          index,
          measured(step) ? step.start : null,
          measured(step) ? step.end : null,
        ]),
      },
    ],
  };
}
